import React from 'react';
import { Container, Row, Col, Card, Badge, Button } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faChartLine,
    faExclamationTriangle, faLightbulb, faRocket, faArrowRight,
    faDesktop, faServer, faDatabase, faChartBar, faRandom,
    faUserMd, faUsers, faClock
} from '@fortawesome/free-solid-svg-icons';
import { Link } from 'react-router-dom';
import SEO from '../SEO/SEO';

import pyramid from "../../assets/images/use-cases/ClinicalTrial-Process.png";
import vis from "../../assets/images/use-cases/ClinicalTrial-Dashboard.png";

const ClinicalTrialPatientFlow = () => {
    return (
        <>
            <SEO
                title="Clinical Trial Patient Flow: AI Digital Twin for Healthcare | Nebulyx AI"
                description="Model your clinical trial as a patient flow network connecting protocol criteria, site capacity, and enrollment funnels. Simulate recruitment scenarios offline to hit enrollment targets faster."
                keywords="AI digital twin, clinical trial, patient recruitment, enrollment, dropout prediction, site performance, healthcare, pharma"
                canonicalUrl="https://nebulyx.ai/use-cases/clinical-trial-patient-flow"
                ogType="article"
            />

            <article className="use-case-page">
                {/* Hero */}
                <header className="use-case-hero">
                    <Container>
                        <Row>
                            <Col className={"w-100"}>
                                <div className={"w-100 text-center"}>
                                    <div >
                                        <Badge className="sector-badge m-1 mb-4 h-100">
                                            <FontAwesomeIcon icon={faUserMd}/>
                                            &nbsp;
                                            Healthcare
                                        </Badge>
                                        <h1 className="use-case-title">Question:&nbsp;How do we accelerate recruitment and reduce dropout?</h1>
                                    </div>
                                </div>
                                <div>
                                    <p className="use-case-subtitle w-100 text-center">
                                        Model your clinical trial as a patient flow network connecting protocol criteria, site capacity, and enrollment funnels. Simulate recruitment scenarios offline, before missing enrollment deadlines.
                                    </p>
                                </div>
                            </Col>
                        </Row>

                        <Row className="hero-stats-inline ">
                            <Col className="stat-item-inline">
                                <FontAwesomeIcon icon={faClock} className="stat-icon"/>
                                <span className="stat-value">40%</span>
                                <span className="stat-label">Faster Enrollment</span>
                            </Col>
                            <Col  className="stat-item-inline">
                                <FontAwesomeIcon icon={faUsers} className="stat-icon"/>
                                <span className="stat-value">-35%</span>
                                <span className="stat-label">Dropout Reduction</span>
                            </Col>
                            <Col  className="stat-item-inline">
                                <FontAwesomeIcon icon={faChartLine} className="stat-icon"/>
                                <span className="stat-value">£2.1M</span>
                                <span className="stat-label">Cost Savings</span>
                            </Col>
                        </Row>

                    </Container>
                </header>

                {/* Executive Summary */}
                <section className="exec-summary-section">
                    <Container>
                        <h2 className="section-title">Executive Summary</h2>
                        <Row className="g-4">
                            <Col md={4}>
                                <Card className="summary-card h-100">
                                    <Card.Body>
                                        <h3 className={"text-start"}><span className={"card-icon-wrapper problem me-1"}><FontAwesomeIcon
                                            sty icon={faExclamationTriangle}/></span>The Problem</h3>
                                        <ul className="summary-list">
                                            <li>80% of trials miss enrollment targets</li>
                                            <li>Unpredictable site performance</li>
                                            <li>High dropout rates mid-study</li>
                                            <li>No early warning for bottlenecks</li>
                                        </ul>
                                    </Card.Body>
                                </Card>
                            </Col>
                            <Col md={4}>
                                <Card className="summary-card h-100">
                                    <Card.Body>
                                        <h3 className={"text-start"}><span
                                            className={"card-icon-wrapper solution me-1"}><FontAwesomeIcon
                                            sty icon={faLightbulb}/></span>The Solution</h3>
                                        <ul className="summary-list">
                                            <li>Build a digital twin of patient flow</li>
                                            <li>Model enrollment funnel end-to-end</li>
                                            <li>Overlay dropout risk prediction</li>
                                            <li>Simulate recruitment interventions</li>
                                        </ul>
                                    </Card.Body>
                                </Card>
                            </Col>
                            <Col md={4}>
                                <Card className="summary-card h-100">
                                    <Card.Body>
                                        <h3 className={"text-start"}><span
                                            className={"card-icon-wrapper benefits me-1"}><FontAwesomeIcon
                                            sty icon={faRocket}/></span>The Benefits</h3>
                                        <ul className="summary-list">
                                            <li>Predict enrollment timeline accurately</li>
                                            <li>Intervene on at-risk patients early</li>
                                            <li>Optimise site selection & capacity</li>
                                            <li>Reduce cost per enrolled patient</li>
                                        </ul>
                                    </Card.Body>
                                </Card>
                            </Col>
                        </Row>
                    </Container>
                </section>

                {/* Process Steps */}
                <section className="content-section process-section">
                    <Container>
                        <h2 className="section-title">The AI Digital Twin Process</h2>
                        <p className="text-center text-muted mb-4">
                            Connect protocol systems, model patient flow as a network, overlay predictive analytics, then simulate recruitment scenarios to hit targets faster.
                        </p>
                        <div className="process-steps-container">
                            <div className="process-step-item">
                                <div className="step-number">1</div>
                                <div className="step-content">
                                    <strong>Connect Data</strong>
                                    <span>Protocol criteria, patient registry, screening logs, site performance</span>
                                </div>
                            </div>
                            <div className="step-arrow">
                                <FontAwesomeIcon icon={faArrowRight}/>
                            </div>
                            <div className="process-step-item">
                                <div className="step-number">2</div>
                                <div className="step-content">
                                    <strong>Model the Funnel</strong>
                                    <span>Map referral → screen → consent → randomise → complete stages</span>
                                </div>
                            </div>
                            <div className="step-arrow">
                                <FontAwesomeIcon icon={faArrowRight}/>
                            </div>
                            <div className="process-step-item">
                                <div className="step-number">3</div>
                                <div className="step-content">
                                    <strong>Overlay Analytics</strong>
                                    <span>Dropout prediction, site velocity, timeline forecasting</span>
                                </div>
                            </div>
                            <div className="step-arrow">
                                <FontAwesomeIcon icon={faArrowRight}/>
                            </div>
                            <div className="process-step-item">
                                <div className="step-number">4</div>
                                <div className="step-content">
                                    <strong>Scenario Analysis</strong>
                                    <span>Simulate adding sites, relaxing criteria, boosting referrals</span>
                                </div>
                            </div>
                        </div>
                    </Container>
                </section>

                {/* AI Digital Twin - Two Sections */}
                <section className="content-section twin-section">
                    <Container>
                        <h2 className="section-title">The AI Digital Twin</h2>
                        <Row className="g-4">
                            {/* Back-end Architecture */}
                            <Col lg={6}>
                                <Card className="twin-view-card h-100">
                                    <Card.Body>
                                        <div className="twin-view-header">
                                            <div className="twin-view-icon backend">
                                                <FontAwesomeIcon icon={faServer}/>
                                            </div>
                                            <h3>Digital Twin Process Flow</h3>
                                        </div>
                                        <p className="twin-view-description">
                                            Connects protocol systems, patient registries, screening databases, and site performance data.
                                            ML models predict dropout risk per patient based on visit compliance, adverse events, and historical patterns,
                                            while timeline projections forecast enrollment completion and identify rate-limiting bottlenecks.</p>
                                        <div className="image-parent">
                                            <img className={"image-child-span w-100 h-100"} src={pyramid} alt=""/>
                                        </div>
                                    </Card.Body>
                                </Card>
                            </Col>

                            {/* Front-end UI */}
                            <Col lg={6}>
                                <Card className="twin-view-card h-100">
                                    <Card.Body>
                                        <div className="twin-view-header">
                                            <div className="twin-view-icon frontend">
                                                <FontAwesomeIcon icon={faDesktop}/>
                                            </div>
                                            <h3>Trial Command Centre</h3>
                                        </div>
                                        <p className="twin-view-description">
                                            Visualises the enrollment funnel from referral to completion with real-time stage counts.
                                            Clinical ops teams can compare site performance, identify at-risk patients for retention outreach,
                                            and simulate recruitment interventions to see projected timeline impact, before committing resources.</p>
                                        <div className="image-parent">
                                            <img className={"image-child-span w-100 h-100"} src={vis} alt=""/>
                                        </div>
                                    </Card.Body>
                                </Card>
                            </Col>
                        </Row>
                        {/* Twin Components */}
                        <Row className="g-4 mt-4">
                            <Col md={4}>
                                <Card className="twin-card h-100">
                                    <Card.Body>
                                        <div className="twin-card-header">
                                            <div className="twin-icon">
                                                <FontAwesomeIcon icon={faDatabase}/>
                                            </div>
                                            <h4>Data Analysed</h4>
                                        </div>
                                        <ul className="twin-list">
                                            <li>Protocol Eligibility Criteria</li>
                                            <li>Patient Registry / EMR</li>
                                            <li>Screening & Consent Logs</li>
                                            <li>Site Enrollment History</li>
                                            <li>Visit Compliance Records</li>
                                            <li>Adverse Event Reports</li>
                                            <li>Query Resolution Times</li>
                                        </ul>
                                    </Card.Body>
                                </Card>
                            </Col>
                            <Col md={4}>
                                <Card className="twin-card h-100">
                                    <Card.Body>
                                        <div className="twin-card-header">
                                            <div className="twin-icon">
                                                <FontAwesomeIcon icon={faChartBar}/>
                                            </div>
                                            <h4>AI / Data Products</h4>
                                        </div>
                                        <ul className="twin-list">
                                            <li>Dropout risk prediction per patient</li>
                                            <li>Enrollment timeline forecasting</li>
                                            <li>Site velocity benchmarking</li>
                                            <li>Screen failure pattern analysis</li>
                                            <li>Protocol deviation hotspots</li>
                                        </ul>
                                    </Card.Body>
                                </Card>
                            </Col>
                            <Col md={4}>
                                <Card className="twin-card h-100">
                                    <Card.Body>
                                        <div className="twin-card-header">
                                            <div className="twin-icon">
                                                <FontAwesomeIcon icon={faRandom}/>
                                            </div>
                                            <h4>Scenarios Tested</h4>
                                        </div>
                                        <ul className="twin-list">
                                            <li>Add 3 new recruitment sites</li>
                                            <li>Relax eligibility criteria</li>
                                            <li>Double referral volume</li>
                                            <li>Site underperformance impact</li>
                                            <li>Patient retention intervention</li>
                                            <li>Protocol amendment effects</li>
                                        </ul>
                                    </Card.Body>
                                </Card>
                            </Col>
                        </Row>

                        {/* Discovery */}
                        <div className="discovery-highlight mt-5">
                            <Row className="align-items-center">
                                <Col lg={8}>
                                    <h3>The Potential Discovery</h3>
                                    <p>
                                        By modelling the trial as a digital twin and running recruitment simulations offline,
                                        clinical operations could discover that adding 3 sites in high-density catchment areas would
                                        accelerate enrollment by 8 weeks, while proactive outreach to the 15% of patients flagged as dropout risks
                                        could retain 40 additional completers.
                                        Simulation may reveal that <strong>relaxing one age criterion increases the eligible pool by 40%</strong> with
                                        only an 11% increase in dropout probability, a tradeoff worth making to hit the enrollment deadline.
                                    </p>
                                </Col>
                                <Col lg={4}>
                                    <div className="discovery-stat">
                                        <span className="discovery-label">Potential Strategy</span>
                                        <span className="discovery-value">8 Weeks Faster + 40 More Completers</span>
                                        <span className="discovery-note">Optimised sites + retention outreach</span>
                                    </div>
                                </Col>
                            </Row>
                        </div>
                    </Container>
                </section>

                {/* CTA */}
                <section className="cta-section">
                    <Container>
                        <div className="cta-content">
                            <h2>Want to Hit Enrollment Targets Faster?</h2>
                            <p>
                                If patient recruitment delays and dropout rates are threatening your trial timeline,
                                let's build your Digital Twin together.
                            </p>
                            <div className="cta-buttons">
                                <Button
                                    variant="light"
                                    size="lg"
                                    href="https://cal.eu/jon-cooke/discovery-call"
                                    className="me-3 mb-2"
                                >
                                    Book a Discovery Call
                                    <FontAwesomeIcon icon={faArrowRight} className="ms-2"/>
                                </Button>
                                <Link to="/use-cases">
                                    <Button variant="outline-light" size="lg" className="mb-2">
                                        View More Use Cases
                                    </Button>
                                </Link>
                            </div>
                        </div>
                    </Container>
                </section>
            </article>
        </>
    );
};

export default ClinicalTrialPatientFlow;